import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban } from 'lucide-react';
import type { OpticalJobDetail, OpticalOrderCancellation, SalesOrderDetail } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useCurrency } from '@/hooks/useCurrency';
import { useCancelOpticalOrderMutation } from '../../api/opticalJobs.api';
import { cancellationBlock, refundablePayments } from '../../tracking/jobs';
import { trackingErrorMessage } from '../../tracking/errors';

interface CancelOrderCardProps {
  order: SalesOrderDetail;
  jobs: readonly OpticalJobDetail[];
  onCancelled: (result: OpticalOrderCancellation) => void;
  /** Named when the order belongs to another branch than the one in use: it is cancelled there, where its refunds leave the till. */
  otherBranchName: string | null;
}

const REASON_MIN = 3;
const REASON_MAX = 500;

/**
 * Cancelling a whole optical order before the lab.
 *
 * Offered only while every job of the order is still ORDERED and nothing else was sold on it; the server decides again. The whole order goes — every pair — never one pair alone. A reference is asked for only for a refund whose tender carries one, never for cash. The reason and any reference live in this dialog's memory and in the request body, nowhere else.
 */
export function CancelOrderCard({
  order,
  jobs,
  onCancelled,
  otherBranchName,
}: CancelOrderCardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const block = cancellationBlock(order, jobs);

  if (block) {
    if (block === 'noJobs' || order.status === 'CANCELLED') return null;
    return (
      <Card>
        <CardHeader title={t('opticalOrders.cancel.title')} />
        <p className="text-muted px-5 py-4 text-sm">{t(`opticalOrders.cancel.blocked.${block}`)}</p>
      </Card>
    );
  }
  if (otherBranchName) {
    return (
      <Card>
        <CardHeader title={t('opticalOrders.cancel.title')} />
        <p className="text-muted px-5 py-4 text-sm">
          {t('opticalOrders.cancel.otherBranch', { branch: otherBranchName })}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t('opticalOrders.cancel.title')}
        subtitle={t('opticalOrders.cancel.hint')}
      />
      <div className="px-5 py-4">
        <Button variant="danger" onClick={() => setOpen(true)}>
          <Ban className="size-4" aria-hidden />
          {t('opticalOrders.cancel.open')}
        </Button>
      </div>
      {open ? (
        <CancelOrderDialog
          order={order}
          jobs={jobs}
          onClose={() => setOpen(false)}
          onCancelled={(result) => {
            setOpen(false);
            onCancelled(result);
          }}
        />
      ) : null}
    </Card>
  );
}

function CancelOrderDialog({
  order,
  jobs,
  onClose,
  onCancelled,
}: {
  order: SalesOrderDetail;
  jobs: readonly OpticalJobDetail[];
  onClose: () => void;
  onCancelled: (result: OpticalOrderCancellation) => void;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const reasonId = useId();
  const [reason, setReason] = useState('');
  const [references, setReferences] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancel = useCancelOpticalOrderMutation(order.id);
  // A second click lands before the button re-renders as busy: only the first may send.
  const inFlight = useRef(false);
  const refunds = refundablePayments(order.payments);

  const trimmed = reason.trim();
  const reasonError =
    trimmed.length < REASON_MIN || trimmed.length > REASON_MAX
      ? t('opticalOrders.cancel.reasonRule')
      : null;
  const missing = refunds.filter(
    (refund) => refund.needsReference && !(references[refund.paymentTransactionId] ?? '').trim(),
  );

  const submit = () => {
    if (inFlight.current) return;
    setError(null);
    if (reasonError || missing.length > 0) {
      setShowErrors(true);
      return;
    }
    inFlight.current = true;
    cancel.mutate(
      {
        reason: trimmed,
        refundReferences: refunds
          .filter((refund) => refund.needsReference)
          .map((refund) => ({
            paymentTransactionId: refund.paymentTransactionId,
            reference: (references[refund.paymentTransactionId] ?? '').trim(),
          })),
      },
      {
        onSettled: () => {
          inFlight.current = false;
        },
        onSuccess: onCancelled,
        onError: (caught) => setError(trackingErrorMessage(t, caught)),
      },
    );
  };

  return (
    <Modal
      isOpen
      onClose={cancel.isPending ? () => undefined : onClose}
      title={t('opticalOrders.cancel.dialogTitle', { reference: order.reference })}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={cancel.isPending}>
            {t('opticalOrders.cancel.keep')}
          </Button>
          <Button variant="danger" onClick={submit} isLoading={cancel.isPending}>
            {t('opticalOrders.cancel.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <div className="space-y-1">
          <p className="text-ink font-medium">{t('opticalOrders.cancel.whatHappens')}</p>
          <ul className="text-ink list-inside list-disc space-y-1">
            <li>
              {t('opticalOrders.cancel.allJobs', { count: jobs.length })}{' '}
              <span className="font-mono" dir="ltr">
                {jobs.map((job) => job.reference).join(', ')}
              </span>
            </li>
            <li>{t('opticalOrders.cancel.stock')}</li>
            {refunds.length > 0 ? <li>{t('opticalOrders.cancel.refunds')}</li> : null}
            <li>{t('opticalOrders.cancel.history')}</li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <label htmlFor={reasonId} className="text-ink font-medium">
            {t('opticalOrders.cancel.reason')}{' '}
            <span aria-hidden className="text-red-600">
              *
            </span>
          </label>
          <textarea
            id={reasonId}
            required
            rows={3}
            maxLength={REASON_MAX}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            aria-invalid={showErrors && reasonError ? true : undefined}
            aria-describedby={`${reasonId}-hint${showErrors && reasonError ? ` ${reasonId}-error` : ''}`}
            className="border-line focus:border-brand-500 focus:ring-brand-200 w-full rounded-lg border px-3 py-2 focus:ring-2 focus:outline-none"
          />
          <p id={`${reasonId}-hint`} className="text-muted text-xs">
            {t('opticalOrders.cancel.reasonHint', { count: trimmed.length })}
          </p>
          {showErrors && reasonError ? (
            <p id={`${reasonId}-error`} role="alert" className="text-xs text-red-700">
              {reasonError}
            </p>
          ) : null}
        </div>

        {refunds.length > 0 ? (
          <fieldset className="space-y-3">
            <legend className="text-ink font-medium">
              {t('opticalOrders.cancel.refundTitle')}
            </legend>
            {refunds.map((refund) => (
              <div
                key={refund.paymentTransactionId}
                className="border-line space-y-2 rounded-lg border p-3"
              >
                <p className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-ink">{t(`sales.methods.${refund.method}`)}</span>
                  <span className="text-ink font-medium" dir="ltr">
                    {format(refund.amountMinor)}
                  </span>
                </p>
                {refund.needsReference ? (
                  <Input
                    label={t('opticalOrders.cancel.refundReference', {
                      method: t(`sales.methods.${refund.method}`),
                    })}
                    required
                    dir="ltr"
                    className="font-mono rtl:text-right"
                    maxLength={64}
                    value={references[refund.paymentTransactionId] ?? ''}
                    onChange={(event) =>
                      setReferences({
                        ...references,
                        [refund.paymentTransactionId]: event.target.value,
                      })
                    }
                    error={
                      showErrors &&
                      missing.some(
                        (entry) => entry.paymentTransactionId === refund.paymentTransactionId,
                      )
                        ? t('opticalOrders.cancel.referenceRequired')
                        : undefined
                    }
                  />
                ) : (
                  <p className="text-muted text-xs">
                    {t('opticalOrders.cancel.noReferenceNeeded')}
                  </p>
                )}
              </div>
            ))}
            <p className="text-muted text-xs">{t('opticalOrders.cancel.refundRule')}</p>
          </fieldset>
        ) : null}
      </div>
    </Modal>
  );
}
